import { Box, Flex, Text, Input, Image, Badge, InputGroup, InputLeftElement, Popover, PopoverTrigger, PopoverContent, PopoverBody, useDisclosure, Table, Thead, Tbody, Tr, Th, Td, TableContainer, PopoverCloseButton, Button, Grid, GridItem, Center, Spinner, IconButton, } from '@chakra-ui/react';
import * as XLSX from 'xlsx';
import MyDiv from './payrollList.style';
import FilterIcon from '../../../../assets/images/Filter.svg';
import DownloadIcon from '../../../../assets/images/DownloadIcon.svg';
import { Search2Icon, InfoOutlineIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import { AlertCard, CustomPagination, CustomToast } from '../../../../components';
import { useNavigate } from 'react-router-dom';
import * as routesNames from '../../../../constants/routes';
import { useEffect } from 'react';
import { CellEditor } from '../CellEditor';
import { IFilterOptions } from '../../../../components/FilterCard/filterCard';
import { ILazyParams, useFetch } from '../../../../hooks/useFetch';
import { IEmployeePayroll } from '../../../../models/payroll';
import FilterCardDropdownOnly from '../../../../components/FilterCard/filterCardDropdownOnly';
import PayrollHeader from '../PayrollHeader';
import { CellEditResponce, emptyFilter, headers, IPayrollData, IPayrollFilter, IStatusCount } from './modal';
import PageInfo from './pageInfo';
import { getCache, setCache } from '../../../../utilities/cache';

const CACHE_KEY = 'payrollListQuery';

const PayrollList = ({ sortOptions, selectedRows, selected, setSelected, setSelectedRows, }: { sortOptions: any, selectedRows: any, selected: any, setSelected: any, setSelectedRows: any }) => {

  const [payrolls, setPayrolls] = useState<IPayrollData>({ data: [], totalCount: 0, loading: false });
  const { addToast } = CustomToast();
  const { fetchApi } = useFetch(addToast);
  const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });
  const [loading, setLoading] = useState(false);


  // Restore cached lazyParams/filter, but always reset "search" so stale search text doesn't silently persist
  const [query, setQuery] = useState<{ lazyParams: ILazyParams, filter: IPayrollFilter, search?: string | null }>(() => {
    const cached = getCache<Partial<{ lazyParams: ILazyParams, filter: IPayrollFilter, search?: string | null }>>(CACHE_KEY, {});
    return {
      lazyParams: cached.lazyParams ?? { first: 0, rows: 500, page: 0, sortField: '', sortOrder: 1 },
      filter: cached.filter ?? emptyFilter,
      search: null,
    };
  });

  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const year = prevMonthDate.getFullYear();
  const month = prevMonthDate.getMonth() + 1;

  const [statusCount, setStatusCount] = useState<IStatusCount[]>([]);
  const [isOpenPageInfoModal, setIsOpenPageInfoModal] = useState<boolean>(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ payrollId: number; newStatus: string; } | null>(null);
  const [runPayrollAlertOpen, setRunPayrollAlertOpen] = useState(false);
  const [markUpdatedAlertOpen, setMarkUpdatedAlertOpen] = useState(false);
  const [reloadAlertOpen, setReloadAlertOpen] = useState(false);

  const handlePageClick = ({ selected }: { selected: number }) => {
    setQuery(prev => {
      const updatedParams = {
        ...prev.lazyParams,
        page: selected,
        first: selected * prev.lazyParams.rows,
      };
      return { ...prev, lazyParams: updatedParams };
    });
  };

  const pageCount = Math.ceil(payrolls.totalCount / query.lazyParams.rows);

  const { isOpen: isFilterOpen, onOpen: openFilter, onClose: closeFilter } = useDisclosure();
  const { isOpen: isSortOpen, onOpen: openSort, onClose: closeSort } = useDisclosure();

  // Persist query (lazyParams + filter + search) to cache whenever it changes
  useEffect(() => {
    setCache(CACHE_KEY, query);
  }, [query]);

  const setRowsPerPage = (rows: number) => {
    setQuery((prev: any) => {
      const updatedParams = {
        ...prev.lazyParams,
        rows,
        page: 0,
        first: 0,
      };
      return { ...prev, lazyParams: updatedParams };
    });
  };

  const statusBody = (rowData: IEmployeePayroll) => {
    const isEditable = rowData.status === "hold" || rowData.status === "updated";
    if (isEditable) {
      return (
        <select style={{ fontWeight: "bold", padding: "2px 4px", borderRadius: "4px" }} value={rowData.status} onChange={(e) => handleStatusChange(rowData.payrollId, e.target.value)}>
          <option value="updated">U</option>
          <option value="hold">H</option>
        </select>
      );
    }
    switch (rowData.status) {
      case "pending": return <div style={{ color: "goldenrod", fontWeight: "bold" }}>P</div>;
      case "generated": return <div style={{ color: "blue", fontWeight: "bold" }}>G</div>;
      case "released": return <div style={{ color: "green", fontWeight: "bold" }}>R</div>;
      case "hold": return <div style={{ color: "red", fontWeight: "bold" }}>H</div>;
      case "updated": return <div style={{ color: "orange", fontWeight: "bold" }}>U</div>;
      default: return <div style={{ color: "gray", fontWeight: "bold" }}>-</div>;
    }
  };

  const handleStatusChange = async (payrollId: number, newStatus: any) => {
    setPendingStatusChange({ payrollId, newStatus });
  };

  const confirmStatusChange = async () => {
    if (loading) {
      addToast({ message: 'Please wait for the current operation to complete.', status: 'warning' });
      return;
    }
    setLoading(true);

    if (!pendingStatusChange) return;

    const { payrollId, newStatus } = pendingStatusChange;

    const validStatus = newStatus as "updated" | "hold";
    const payload = { payrollId, status: validStatus };

    const response = await fetchApi('Payroll/UpdateStatus', 'PUT', payload);

    if (response) {
      addToast({ message: 'Status updated successfully.', status: 'success' });
      setPayrolls(prev => ({
        ...prev,
        data: prev.data.map(p =>
          p.payrollId === payrollId ? { ...p, status: validStatus } : p
        )
      }));
    }

    setPendingStatusChange(null);
    setLoading(false);
  };

  const cancelStatusChange = () => {
    setPendingStatusChange(null);
  };

  async function loadPayrolls() {
    if (query.filter.year && query.filter.month && query.filter.branchId) {

      if (payrolls.loading) {
        addToast({ message: 'Please wait before trying again.', status: 'warning' });
        return;
      }
      setPayrolls(prev => ({ ...prev, loading: true }));

      const _query = new URLSearchParams({
        lazyParams: JSON.stringify(query.lazyParams),
        filter: JSON.stringify(query.filter ?? {}),
        search: query.search ?? ''
      }).toString();

      const result = await fetchApi<any>('Payroll', 'GET', null, _query);
      if (result) {
        if (result.data.length === 0) {
          addToast({ message: "No payrolls found.", status: "info" });
        }
        setPayrolls(prev => ({ ...prev, data: result.data, totalCount: result.totalRecords }));
      }
      const result2 = await fetchApi<IStatusCount[]>('Payroll/status', 'GET', null, _query);
      if (result2) {
        setStatusCount(result2);
      }
      setPayrolls(prev => ({ ...prev, loading: false }));
    } else {
      setPayrolls({ data: [], totalCount: 0, loading: false });
      setStatusCount([]);
      addToast({ message: 'Apply year, month and branch filter to load data.', status: 'info' });
    }
    setReloadAlertOpen(false);
  }

  async function loadPayrollFilter() {
    const response = await fetchApi<IFilterOptions>('Filters/PayrollFilter', 'GET');
    if (response) {
      setFilter(response);
    }
  }

  useEffect(() => {
    loadPayrollFilter();
  }, []);

  useEffect(() => {
    loadPayrolls();
  }, [query]);

  const handleDownloadClick = () => {
    const selectedRowData = selectedRows.filter((row: { id: any; }) =>
      selectedRows.includes(row.id)
    );

    const formattedData = selectedRowData.map((row: any) => ({
      "Emp. ID": row.empId,
      Name: row.name,
      Period: row.period,
      "Effective Work Days": row.ewd,
      "Net Payable Days": row.npd,
      LOP: row.lop,
      "Gross Salary": row.grosssalary,
      Incentive: row.incentive,
      Deductions: row.deduction,
      "Net Pay": row.netpay,
      Status: row.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Selected_Payroll");
    XLSX.writeFile(workbook, "selected_payroll.xlsx");
  };

  const navigate = useNavigate();
  const handleDummyData = (employeeId: number) => {
    navigate(routesNames.SALARYHISTORY, { state: { employeeId } });
  }

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = e.currentTarget.value;
      setQuery(q => ({ ...q, search: value }));
    }
  };

  const handleSelect = (option: string) => {
    setSelected(option);
    let sorted = [...payrolls.data];
    switch (option) {
      case 'Sort by Employee Name A-Z':
        sorted.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
        break;
      case 'Sort by Employee Name Z-A':
        sorted.sort((a, b) => b.employeeName.localeCompare(a.employeeName));
        break;
      case 'Sort by Gross Salary Low to High':
        sorted.sort((a, b) => a.netSalary - b.netSalary);
        break;
      case 'Sort by Gross Salary High to Low':
        sorted.sort((a, b) => b.netSalary - a.netSalary);
        break;
      case 'Sort by Net Pay High to Low':
        sorted.sort((a, b) => b.totalEarnings - a.totalEarnings);
        break;
      case 'Sort by Net Pay Low to High':
        sorted.sort((a, b) => a.totalEarnings - b.totalEarnings);
        break;
      case 'Sort by Effective Work Days Low to High':
        sorted.sort((a, b) => a.paidDays - b.paidDays);
        break;
      case 'Sort by Effective Work Days High to Low':
        sorted.sort((a, b) => b.paidDays - a.paidDays);
        break;
      default:
        break;
    }
    closeSort();
  };

  const runPayroll = async () => {
    const response = await fetchApi<{ message: string }>("Payroll/run-payroll", "POST", null, `branchId=${query.filter.branchId}`);
    if (response) {
      addToast({ message: response.message, status: 'success' });
      loadPayrolls();
    }
    setRunPayrollAlertOpen(false);
  }
  const markUpdated = async () => {
    if (loading) {
      addToast({ message: 'Please wait for the current operation to complete.', status: 'warning' });
      return;
    }
    setLoading(true);
    const response = await fetchApi<{ message: string }>("Payroll/mark-updated", "POST", null, `branchId=${query.filter.branchId}`);
    if (response) {
      addToast({ message: response.message, status: 'success' })
      loadPayrolls();
    };
    setMarkUpdatedAlertOpen(false);
    setLoading(false);
  }

  const onCellEditComplete = async (payrollId: number, field: string, updatedValue: number) => {
    if (loading) {
      addToast({ message: 'Please wait for the current operation to complete.', status: 'warning' });
      return;
    }
    setLoading(true);
    const res = await fetchApi<CellEditResponce>(`Payroll/Update/${payrollId}`, "POST", { field, value: updatedValue }, null, 'Value Updated');
    if (res) {
      setPayrolls(prev => {
        const updatedData = prev.data.map(p => {
          if (p.payrollId === payrollId) {
            return {
              ...p,
              [field]: res.value,
              totalEarnings: res.totalEarnings,
              totalDeductions: res.totalDeductions,
              netSalary: res.netSalary,
              status: res.status
            };
          }
          return p;
        });

        return { ...prev, data: updatedData };
      });
    }
    setLoading(false);
  }

  const tdbody = (p: IEmployeePayroll, field: keyof (IEmployeePayroll)) => {
    if (p.status === 'pending') {
      return '--';
    } else if (p.status === 'released') {
      return p[field];
    }
    const val = p[field];
    return <CellEditor value={typeof (val) === 'number' ? val : 0} onValueUpdate={(newValue: number) => onCellEditComplete(p.payrollId, field, newValue)} />
  }

  const totalPresent = (p: IEmployeePayroll) => {
    return (p.paidDays - p.entitled);
  }

  // Resolve the currently active Status filter (a string like 'pending') back to its dropdown id,
  // so the FilterCard UI reflects what's actually applied (including after cache restore).
  const activeStatusId = filter.filterOptions['Status']?.find(
    (s) => s.name.toLowerCase() === (query.filter.status ?? '')
  )?.id ?? 0;

  return (
    <MyDiv>
      <Grid className="headerGrid" mb={4}>
        <GridItem >
          <PayrollHeader totalRecords={payrolls.totalCount} statusCount={statusCount} />
        </GridItem>
      </Grid>
      <Flex className=" flex_header">
        <Text className="font-poppins text_lg text_medium " color={'#000000 '}>Payroll List ({`${query.filter.month}/${query.filter.year} - ${filter.filterOptions['Branch']?.find(f => f.id === query.filter.branchId)?.name ?? 'Select Branch'}`})</Text>
        {/* Right: Filters/Search/etc. */}
        <Flex className="filter_section" gap={2}>
          {/* Search */}
          <InputGroup maxWidth={'fit-content  '} gap="10px">
            <InputLeftElement pointerEvents="none">
              <Search2Icon color="rgba(102, 112, 133, 1)" />
            </InputLeftElement>
            <Input type="text" placeholder="Search Employee by name...." defaultValue={query.search ?? ''} onKeyDown={handleSearch} />
          </InputGroup>

          {/* Filter Popover */}
          <Popover placement="bottom-end" closeOnBlur={false} isOpen={isFilterOpen} onOpen={openFilter} onClose={closeFilter} >
            <PopoverTrigger>
              <Image src={FilterIcon} alt="Filter" className="icon_btn" onClick={closeSort} />
            </PopoverTrigger>
            <FilterCardDropdownOnly
              filter={filter}
              defaulValues={{
                "Year": [query.filter.year],
                "Month": [query.filter.month],
                "Branch": [query.filter.branchId ?? 0],
                "Department": [query.filter.departmentId ?? 0],
                "Status": [activeStatusId],
              }}
              onApplyFilter={(filters) => {
                const _filter: IPayrollFilter = { ...emptyFilter };
                Object.keys(filters).forEach((key) => {
                  if (filters[key].length > 0) {
                    if (key === "Year")
                      _filter.year = filters[key][0];
                    if (key === "Month")
                      _filter.month = filters[key][0];
                    // if (key === "Company")
                    //   _filter.companyId = filters[key][0];
                    if (key === "Branch")
                      _filter.branchId = filters[key][0];
                    if (key === "Department")
                      _filter.departmentId = filters[key][0];
                    if (key === "Status") {
                      const statusFilter = filter.filterOptions[key];
                      const currentStatus = statusFilter.find(s => s.id === filters[key][0])?.name.toLowerCase() ?? '';
                      if (currentStatus === 'pending') _filter.status = 'pending';
                      else if (currentStatus === 'generated') _filter.status = 'generated';
                      else if (currentStatus === 'updated') _filter.status = 'updated';
                      else if (currentStatus === 'released') _filter.status = 'released';
                      else if (currentStatus === 'hold') _filter.status = 'hold';
                      else _filter.status = undefined;
                    }
                  }
                });
                setQuery(prev => { return { ...prev, lazyParams: { ...prev.lazyParams, page: 0, first: 0 }, filter: _filter }; });
              }}
              onClose={closeFilter}
            />
          </Popover>

          {/* Sort Popover */}
          <Popover placement="bottom-end" closeOnBlur={false} isOpen={isSortOpen} onOpen={openSort} onClose={closeSort}>
            <PopoverContent w="270px" borderRadius="10px" boxShadow="md">
              <Box px={4} py={2} borderBottom="1px solid #eee">
                <Text className="font-poppins text_sm text_medium" textAlign="center" color="#00000080">Sort By</Text>
                <PopoverCloseButton top="8px" right="12px" />
              </Box>
              <PopoverBody p={0} maxH="250px" overflowY="auto">
                {sortOptions.map((option: any) => (
                  <Box key={option} px={4} py={2} cursor="pointer" bg={selected === option ? 'white' : 'white'} _hover={{ bg: 'gray.300' }}
                    onClick={() => handleSelect(option)} >
                    <Text fontSize="14px" fontWeight="500">{option}</Text>
                  </Box>
                ))}
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* Download Icon (only when rows selected) */}
          {selectedRows.length > 0 && (
            <Image src={DownloadIcon} alt="Download" className="icon_btn" cursor="pointer" onClick={handleDownloadClick} />
          )}

          {query.filter.year && query.filter.month && query.filter.branchId &&
            (<>
              {query.filter.year === year && query.filter.month === month && payrolls.data.find(f => f.status === "pending") && <Button size='sm' colorScheme="green" variant="solid" onClick={() => setRunPayrollAlertOpen(true)}>Run-Payroll</Button>}
              {query.filter.year === year && query.filter.month === month && <Button size='sm' colorScheme="orange" variant="solid" onClick={() => setMarkUpdatedAlertOpen(true)}>Mark Updated</Button>}
              <Button size='sm' colorScheme="blue" variant="solid" onClick={() => setReloadAlertOpen(true)}>Reload</Button>
              <IconButton size="sm" colorScheme="purple" variant="solid" icon={<InfoOutlineIcon />} aria-label="Payroll Summary" onClick={() => setIsOpenPageInfoModal(true)} />
            </>)}
        </Flex>
      </Flex>
      <Grid className="grid_container">
        <GridItem colSpan={12}>
          <Box className="page_wrapper">
            <Box position="relative" minH="300px">
              {payrolls.loading ? (
                <Center position="absolute" top={0} left={0} right={0} bottom={0}>
                  <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
                </Center>
              ) : (
                <>
                  {payrolls.data.length > 0 ? (
                    <Box className="table_wrapper" mt={2}>
                      <TableContainer borderRadius="lg" boxShadow="sm" bg="#fff">
                        <Table variant='striped' >
                          <Thead bg="#f9fafb">
                            <Tr>
                              {headers.map((header) => (
                                <Th key={header.id} className="font-poppins text_medium text_sm">{header.name}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {payrolls.data.map((emp, index: any) => (
                              <Tr key={emp.employeeId} className={selectedRows.includes(emp.employeeId) ? 'selected' : ''}>
                                <Td>
                                  <Badge className="empId" onClick={() => { handleDummyData(emp.employeeId) }}> {emp.employeeCode} </Badge>
                                </Td>
                                <Td className='font-poppins text_regular text_sm'>{`${emp.employeeName} (${emp.departmentName?.substring(0, 3)})`}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.basic}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'earned_Basic')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.hra}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'earned_HRA')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.conveyance_Allowance}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'earned_Conveyance_Allowance')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'incentive')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'otherEarning')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'employee_PF')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'employer_PF')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'esic_Employee')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'esic_Employer')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'health_Insurance_Amount')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'meal_Allowance_Amount')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'transport_Allowance_Amount')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'tds')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{tdbody(emp, 'otherDeduction')}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.netSalary}</Td>
                                <Td className='font-poppins text_regular text_sm'>{totalPresent(emp)}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.absentDays + (emp.halfDays / 2)}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.balance_Leave}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.entitled}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.lop}</Td>
                                <Td className='font-poppins text_regular text_sm'>{emp.paidDays}</Td>
                                <Td className='font-poppins text_regular text_sm'>{statusBody(emp)}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ) : (
                    <Flex justify="center" align="center" height="400px" color="gray.500" >
                      <Text className="font-poppins text_regular text_md">No Data Available</Text>
                    </Flex>
                  )}
                </>)}
            </Box>
            <CustomPagination pageCount={pageCount} rowsPerPageOptions={[50, 100, 200, 500]} handlePageClick={handlePageClick} setRowsPerPage={setRowsPerPage} rowsPerPage={query.lazyParams.rows} totalRecords={payrolls.totalCount} currentPage={query.lazyParams.page} />

          </Box>
        </GridItem>
      </Grid>

      <AlertCard isOpen={pendingStatusChange !== null} onClose={cancelStatusChange} onConfirm={confirmStatusChange} title="Confirm Status Change" description={`Are you sure you want to change status to "${pendingStatusChange?.newStatus.toUpperCase()}"?`} confirmLabel="Confirm" colorScheme="blue" />

      <AlertCard isOpen={runPayrollAlertOpen} onClose={() => setRunPayrollAlertOpen(false)} onConfirm={runPayroll} title="Confirm Run Payroll" description={`Are you sure you want to run payroll`} confirmLabel="Confirm" colorScheme="green" />

      <AlertCard isOpen={markUpdatedAlertOpen} onClose={() => setMarkUpdatedAlertOpen(false)} onConfirm={markUpdated} title="Confirm Mark Updated" description={`Are you sure you want to mark as updated`} confirmLabel="Confirm" colorScheme="orange" />

      <AlertCard isOpen={reloadAlertOpen} onClose={() => setReloadAlertOpen(false)} onConfirm={loadPayrolls} title="Confirm Reload Payrolls" description={`Are you sure you want to reload payrolls`} confirmLabel="Confirm" colorScheme="blue" />

      {/* Page Info Modal */}
      <PageInfo isOpen={isOpenPageInfoModal} onClose={() => setIsOpenPageInfoModal(false)} />
    </MyDiv >
  );
};

export default PayrollList;
